import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

import { onBeforeUnmount, ref, watch, type CSSProperties } from "vue";
import AppButton from "../ui-kit/button/AppButton.vue";
import AppIcon from "../ui-kit/icon/AppIcon.vue";

type DropdownProps = {
  position?: "left" | "right";
  label?: string;
  disabled?: boolean;
  width?: string;
  type: string;
};

type DropdownEmits = {
  "on-close": void;
  "select": [option: string];
};

const OPEN_DELAY_MS = 300;


export default /*@__PURE__*/_defineComponent({
  __name: 'FileDropdown',
  props: {
    position: { default: "left" },
    label: { default: "Menu" },
    disabled: { type: Boolean, default: false },
    width: { default: "" },
    type: { default: "default" }
  },
  emits: ["on-close", "select"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const wrapperRef = ref<HTMLElement | null>(null);
let closeTimeout: ReturnType<typeof setTimeout> | null = null;
const dropdownStyles = ref<CSSProperties>({
  position: "fixed",
  top: "0px",
  left: "0px",
  minWidth: "0px",
  opacity: "0",
});
let openTimeout: ReturnType<typeof setTimeout> | null = null;
const handleSelect = (option: string) => {
  closeDropdown();
  emit("select", option);
};

watch(isOpen, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      updatePosition();
      window.addEventListener("scroll", handleScroll);
      document.addEventListener("click", handleClickOutside);
    }, 50);
  } else {
    window.removeEventListener("scroll", handleScroll);
    document.removeEventListener("click", handleClickOutside);
  }
});

const handleClickOutside = (event: MouseEvent) => {
  if (!isOpen.value || !wrapperRef.value) return
  const target = event.target as HTMLElement

  if (target.closest('.dropdown-content')) return

  if (!wrapperRef.value.contains(target)) {
    closeDropdown()
  }
}

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  document.removeEventListener("click", handleClickOutside);
  if (closeTimeout) {
    clearTimeout(closeTimeout);
  }
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const resetDropdownState = () => {
  if (openTimeout) {
    clearTimeout(openTimeout);
    openTimeout = null;
  }

  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }

  isOpen.value = false;
};

const openDropdown = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }

  if (openTimeout) {
    clearTimeout(openTimeout);
  }

  openTimeout = setTimeout(() => {
    isOpen.value = true;
    openTimeout = null;
  }, OPEN_DELAY_MS);
};

const closeMouseDropdown = (event?: MouseEvent) => {
  if (openTimeout) { clearTimeout(openTimeout); openTimeout = null }
  if (closeTimeout) { clearTimeout(closeTimeout) }

  closeTimeout = setTimeout(() => {
    const hovered = document.querySelectorAll(':hover')
    const isOverDropdownContent = Array.from(hovered).some(
      el => el.classList.contains('dropdown-content')
    )
    if (isOverDropdownContent) {
      closeTimeout = null
      return
    }
    isOpen.value = false
    closeTimeout = null
  }, 1000)
}

const closeDropdown = () => {
  resetDropdownState();
};

__expose({ closeDropdown });

const updatePosition = () => {
  const triggerEl = triggerRef.value;
  const dropdownEl = dropdownRef.value;

  if (!triggerEl || !dropdownEl) return;

  dropdownEl.style.visibility = "hidden";

  const triggerRect = triggerEl.getBoundingClientRect();
  const dropdownRect = dropdownEl.getBoundingClientRect();

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const spaceBelow = viewportHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;

  const openDownward =
    spaceBelow >= dropdownRect.height ||
    (spaceBelow >= spaceAbove && spaceBelow > 50);
  const openUpward = !openDownward;

  let top: number;

  if (openDownward) {
    top = triggerRect.bottom + window.scrollY;

    if (top + dropdownRect.height > viewportHeight - 10) {
      top = viewportHeight - dropdownRect.height - 10;
    }

    if (props.type == "menu") {
      top -= 18;
    }
  } else {
    top = triggerRect.top + window.scrollY - dropdownRect.height;

    if (top < 10) {
      top = 10;
    }

    if (props.type == "menu") {
      top += 18;
    }
  }

  if (openDownward && props.type === "default") {
    top += 40;
  } else if (openUpward && props.type === "default") {
    top -= 40;
  }

  let left;

  if (props.position === "left") {
    left = triggerRect.left + window.scrollX;

    if (left + dropdownRect.width > viewportWidth - 10) {
      left = viewportWidth - dropdownRect.width - 10;
    }
    if (left < 10) {
      left = 10;
    }
  } else {
    left = triggerRect.right + window.scrollX - dropdownRect.width;
    if (left < 10) {
      left = 10;
    }
    if (left + dropdownRect.width > viewportWidth - 10) {
      left = viewportWidth - dropdownRect.width - 10;
    }
  }

  if (props.type === "menu") {
    left += 15;
  }

  dropdownEl.style.visibility = "visible";

  dropdownStyles.value = {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    minWidth: `${triggerRect.width}px`,
    opacity: "1",
  };

  dropdownEl.classList.toggle("app-dropdown__window--upward", openUpward);
  dropdownEl.classList.toggle("app-dropdown__window--downward", openDownward);
};

const handleScroll = () => {
  if (isOpen.value) {
    closeDropdown();
  }
};

watch(isOpen, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      updatePosition();
      window.addEventListener("scroll", handleScroll);
    }, 50);
  } else {
    window.removeEventListener("scroll", handleScroll);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "app-dropdown",
    ref_key: "wrapperRef",
    ref: wrapperRef,
    onMouseenter: openDropdown,
    onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (closeMouseDropdown($event)))
  }, [
    _createElementVNode("div", {
      ref_key: "triggerRef",
      ref: triggerRef
    }, [
      (_ctx.type == 'default')
        ? (_openBlock(), _createBlock(AppButton, {
            key: 0,
            label: _ctx.label,
            disabled: _ctx.disabled,
            active: isOpen.value,
            width: _ctx.width,
            type: "dropdown",
            onClick: toggleDropdown
          }, null, 8, ["label", "disabled", "active", "width"]))
        : _createCommentVNode("", true),
      (_ctx.type == 'menu')
        ? (_openBlock(), _createBlock(AppIcon, {
            key: 1,
            name: "options-menu",
            width: "16px",
            color: "#979797",
            cursor: "pointer",
            "hover-color": "#499259"
          }))
        : _createCommentVNode("", true)
    ], 512),
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "dropdownRef",
          ref: dropdownRef,
          class: _normalizeClass(["app-dropdown__window", [`app-dropdown__window-${_ctx.type}`]]),
          style: _normalizeStyle(dropdownStyles.value)
        }, [
          _renderSlot(_ctx.$slots, "default", { onSelect: handleSelect })
        ], 6))
      : _createCommentVNode("", true)
  ], 544))
}
}

})